import React, { useState, useEffect, useCallback } from 'react';

const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
const fmtD = (d) => d ? new Date(d).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

export default function AutoFriendlies() {
  const [stats, setStats] = useState({});
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryDetail, setEntryDetail] = useState(null);
  const [showSearch, setShowSearch] = useState(true);
  const [searchParams, setSearchParams] = useState({ email: '', phone: '', bin: '', last4: '', name: '', address: '', city: '', zip: '', state: '' });
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', perPage);
      params.set('offset', page * perPage);

      const [statsRes, entriesRes] = await Promise.all([
        fetch('/api/friendly-queue/stats', { credentials: 'include' }).then(r => r.json()),
        fetch(`/api/friendly-queue?${params}`, { credentials: 'include' }).then(r => r.json()),
      ]);

      setStats(statsRes);
      setEntries(entriesRes.entries || []);
      setTotal(entriesRes.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter, perPage, page]);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 8000); return () => clearInterval(i); }, [fetchData]);
  useEffect(() => { setPage(0); }, [statusFilter, perPage]);

  const handleEntryClick = async (id) => {
    if (selectedEntry === id) { setSelectedEntry(null); setEntryDetail(null); return; }
    try {
      const res = await fetch(`/api/friendly-queue/${id}`, { credentials: 'include' }).then(r => r.json());
      setSelectedEntry(id);
      setEntryDetail(res);
    } catch (err) { console.error(err); }
  };

  const sBadge = (s) => {
    const c = { pending: 'bg-yellow-100 text-yellow-800', processing: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800', no_cardholder: 'bg-orange-100 text-orange-800' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[s] || 'bg-gray-100 text-gray-800'}`}>{s}</span>;
  };
  const tBadge = (t) => {
    const c = { visa: 'bg-blue-100 text-blue-700', mastercard: 'bg-orange-100 text-orange-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[t] || 'bg-gray-100 text-gray-700'}`}>{t?.toUpperCase()}</span>;
  };

  const totalPages = Math.ceil(total / perPage);
  const gwStats = stats.gatewayStats || [];
  const e = entryDetail?.entry;
  const ch = entryDetail?.cardholder;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading...</div></div>;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">🇺🇸 Auto Friendlies</h1>
        <p className="text-sm text-gray-500">Automatic USA transactions triggered by Rebill Engine success</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { l: 'Total', v: stats.total || 0, c: 'text-gray-900' },
          { l: 'Pending', v: stats.pending || 0, c: 'text-yellow-600' },
          { l: 'Completed', v: stats.completed || 0, c: 'text-green-600' },
          { l: 'Failed', v: stats.failed || 0, c: 'text-red-600' },
          { l: 'No Card', v: stats.noCardholder || 0, c: 'text-orange-600' },
          { l: 'Today ✅', v: stats.todayCompleted || 0, c: 'text-green-600' },
          { l: 'Today ❌', v: stats.todayFailed || 0, c: 'text-red-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <p className="text-xs text-gray-500 uppercase">{s.l}</p>
            <p className={`text-2xl font-bold ${s.c}`}>{s.v}</p>
          </div>
        ))}
      </div>

      {/* Gateway Distribution Stats */}
      {gwStats.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">📊 Per-Gateway Distribution</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {gwStats.map((gw, i) => {
              const priceStats = JSON.parse(gw.priceStats || '{}');
              const visaPct = gw.total > 0 ? ((gw.visaCount / gw.total) * 100).toFixed(1) : '0';
              const mcPct = gw.total > 0 ? ((gw.mcCount / gw.total) * 100).toFixed(1) : '0';
              return (
                <div key={i} className="bg-gray-50 rounded-lg p-3 border">
                  <p className="text-xs font-bold text-gray-700 truncate">{gw.gwName}</p>
                  <p className="text-lg font-bold text-gray-900">{gw.total} txns</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-blue-600">VISA {visaPct}%</span>
                    <span className="text-xs text-orange-600">MC {mcPct}%</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {Object.entries(priceStats).map(([price, count]) => (
                      <span key={price} className="text-[10px] bg-gray-200 rounded px-1">${price}: {count}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search Transactions */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">Search Transactions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {[
            ['email', 'Email', 'john@example.com'],
            ['phone', 'Phone', '212-555-...'],
            ['bin', 'First 6 (BIN)', '411111'],
            ['last4', 'Last 4', '1234'],
            ['name', 'Cardholder Name', 'John Doe'],
            ['address', 'Address', '123 Main St'],
            ['city', 'City', 'New York'],
            ['state', 'State', 'NY'],
            ['zip', 'ZIP', '10001'],
          ].map(([key, label, placeholder]) => (
            <div key={key}>
              <label className="text-[10px] text-gray-500 uppercase">{label}</label>
              <input type="text" value={searchParams[key]} onChange={(ev) => setSearchParams({ ...searchParams, [key]: ev.target.value })} placeholder={placeholder} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none" />
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={async () => { const has = Object.values(searchParams).some(v => v.trim()); if (!has) { alert('Enter at least one search parameter'); return; } setSearching(true); try { const p = new URLSearchParams(); Object.entries(searchParams).forEach(([k,v]) => { if (v.trim()) p.set(k, v.trim()); }); const r = await fetch(`/api/friendly-queue/search?${p}`, { credentials: 'include' }); const d = await r.json(); setSearchResults(d.results || []); } catch(err){console.error(err);} finally{setSearching(false);} }} disabled={searching} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">{searching ? '⏳ Searching...' : 'Search'}</button>
          <button onClick={() => { setSearchParams({ email:'', phone:'', bin:'', last4:'', name:'', address:'', city:'', zip:'', state:'' }); setSearchResults(null); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-xs hover:bg-gray-300">Clear</button>
        </div>
        {searchResults !== null && <div className="text-xs font-medium text-blue-600">Showing {searchResults.length} search result(s) in the table below</div>}
      </div>

      {/* Entry Detail Panel — appears after search when a transaction is clicked */}
      {selectedEntry && e && (
        <div className="bg-white rounded-lg border-2 border-blue-300 p-5 shadow-md space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-bold text-blue-600">Transaction Detail — {e.id?.substring(0, 8)}...</h3>
            <button onClick={() => { setSelectedEntry(null); setEntryDetail(null); }} className="text-xs text-gray-400 hover:text-gray-600">✕ Close</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {[['Status', sBadge(e.status)], ['Source', <span className="font-medium text-purple-600">{e.source}</span>], ['Source TxnID', <span className="font-mono text-gray-600">{e.sourceTxnId || '—'}</span>], ['Source Amount', <span className="font-bold text-gray-900">{fmt(e.sourceAmount)}</span>], ['Card Type', tBadge(e.cardType)], ['Price Point', <span className="font-bold text-green-600">{fmt(e.pricePoint)}</span>], ['Gateway', <span className="text-gray-700">{e.gwName}</span>], ['Processor-Bank', <span className="font-mono text-gray-600">{e.gwProcessor}-{e.gwBank}</span>], ['NMI TxnID', <span className="font-mono text-gray-600">{e.nmiTxnId || '—'}</span>], ['NMI Response', <span className={e.nmiResponseCode === '100' ? 'text-green-600' : 'text-red-600'}>{e.nmiResponseCode} {e.nmiResponseText}</span>], ['Created', fmtD(e.createdAt)], ['Processed', fmtD(e.processedAt)]].map(([label, val], i) => (
              <div key={i} className="bg-gray-50 rounded px-3 py-2"><div className="text-[10px] text-gray-500 uppercase">{label}</div><div className="mt-0.5">{val}</div></div>
            ))}
          </div>
          {ch && (
            <div className="border-t pt-3">
              <h4 className="text-xs font-bold text-gray-600 mb-2">Cardholder Used</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                {[['Name', `${ch.firstName} ${ch.lastName}`], ['Email', ch.email], ['Phone', ch.phone], ['Card', `${ch.cardNumber}`], ['Last 4', ch.cardLast4], ['CVV', ch.cvv], ['Expiry', `${ch.expiryMonth}/${ch.expiryYear}`], ['Card Type', ch.cardType?.toUpperCase()], ['Card BIN', e.cardholderBin || ch.cardNumber?.substring(0, 6) || '—'], ['ProgramGuid', ch.programGuid || '—'], ['IP', ch.ipAddress], ['Address', ch.address], ['City', ch.city], ['State', ch.state], ['ZIP', ch.zipCode], ['Bluebanc ID', ch.bluebancCardId || '—']].map(([label, val], i) => (
                  <div key={i} className="bg-blue-50 rounded px-2 py-1.5"><div className="text-[10px] text-gray-500">{label}</div><div className="font-mono text-gray-800 break-all text-[11px]">{val || '—'}</div></div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">Filter:</span>
          {['', 'pending', 'completed', 'failed', 'no_cardholder'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {s === '' ? 'All' : s === 'no_cardholder' ? 'No Card' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <select value={perPage} onChange={e => setPerPage(parseInt(e.target.value))} className="bg-gray-100 text-gray-700 text-xs rounded-lg px-3 py-1.5 border border-gray-200">
          {[25, 50, 100, 250].map(n => <option key={n} value={n}>{n}/page</option>)}
        </select>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <span className="text-sm font-semibold text-gray-700">{searchResults ? `🔍 Search Results (${searchResults.length})` : `Transactions (${total})`}</span>
          <div className="flex items-center space-x-2">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs disabled:opacity-30">← Prev</button>
            <span className="text-xs text-gray-500">{page + 1}/{totalPages || 1}</span>
            <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs disabled:opacity-30">Next →</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-500 uppercase">
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Card Type</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Gateway</th>
                <th className="px-3 py-2 text-left">Cardholder</th>
                <th className="px-3 py-2 text-left">Last 4</th>
                <th className="px-3 py-2 text-left">BIN (6)</th>
                <th className="px-3 py-2 text-left">NMI TxnID</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(searchResults || entries).length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">{searchResults ? 'No results found' : 'No entries yet'}</td></tr>
              ) : (searchResults || entries).map(entry => (
                <tr key={entry.id} className={`hover:bg-blue-50 cursor-pointer transition-colors ${selectedEntry === entry.id ? 'bg-blue-50' : ''}`} onClick={() => handleEntryClick(entry.id)}>
                  <td className="px-3 py-2 text-gray-600">{fmtD(entry.createdAt)}</td>
                  <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-100 text-purple-700">{entry.source}</span></td>
                  <td className="px-3 py-2">{tBadge(entry.cardType)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-gray-900">{fmt(entry.pricePoint)}</td>
                  <td className="px-3 py-2 text-gray-600 max-w-[160px] truncate" title={entry.gwName}>{entry.gwName}</td>
                  <td className="px-3 py-2 text-gray-700">{entry.cardholderName || '—'}</td>
                  <td className="px-3 py-2 font-mono text-gray-600">{entry.cardholderLast4 || '—'}</td>
                  <td className="px-3 py-2 font-mono text-gray-600">{entry.cardholderBin || '—'}</td>
                  <td className="px-3 py-2 font-mono text-gray-500">{entry.nmiTxnId || '—'}</td>
                  <td className="px-3 py-2">{sBadge(entry.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-2 border-t border-gray-200 flex justify-end space-x-2">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs disabled:opacity-30">← Prev</button>
            <span className="text-xs text-gray-500">{page + 1}/{totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs disabled:opacity-30">Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
